import React, { useState, useEffect, useContext } from "react";
import CartTable from "./CartTable";
import CartHeader from "./CartHeader";
import CartSummary from "./CartSummary";
import HoldBillDialog from "./HoldBillDialog";
import OpenHoldsPanel from "./OpenHoldsPanel";
import KOTDialog from "./KOTDialog";
import useStyles from "./CartStyles";
import { StaffCommissionDetailContext } from "../Context/StaffCommissionDetailsContext";
import { useCart } from "../Context/CartContext";
import { useOrderSession, getCartItemsFromSession } from "../Context/OrderSessionContext";
import { useSnackbar } from "../utils/useSnackbar";
import { useHistory, useLocation } from "react-router-dom";
import { useDiscount } from "../Context/DiscountContext";
import { useTheme } from "@material-ui/core/styles";
import { Button, Drawer, Divider, CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import FolderOpenOutlinedIcon from "@material-ui/icons/FolderOpenOutlined";
import RestaurantMenuOutlinedIcon from "@material-ui/icons/RestaurantMenuOutlined";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ShoppingBasketOutlinedIcon from "@material-ui/icons/ShoppingBasketOutlined";
import DeleteSweepOutlinedIcon from "@material-ui/icons/DeleteSweepOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import usePermissions from "../utils/permissions";
import { useEffectiveProfile } from "../utils/useEffectiveProfile";
import { useConfirmation } from "../Context/ConfirmationContext";
import { formatInvoiceNumber } from "../utils/docNumber";

// These used to carry a "top-center" position plus backgroundColor/style
// fields from the pre-react-toastify snackbar library - the color/style
// fields are silently ignored by the current hook (type/color now comes
// from the message text via useSnackbar's own inference + the app's
// "colored" theme, see ItemCards.js), and "top-center" just made this one
// screen's toasts appear somewhere different from the rest of the app's
// shared bottom-right default.

function Cart({ open, handleDrawerClose, currency, invoice, invoices, isCartPersistent }) {
  const classes = useStyles();
  const {
    staffCommissionDetail,
    setStaffCommissionDetail,
    staffCommissionDetailsArray,
    setStaffCommissionDetailsArray,
  } = useContext(StaffCommissionDetailContext);
  const theme = useTheme();
  const { cartItems, setCartItems, setVat, hydrateInvoiceCart, clearInvoiceStockCredit } = useCart();
  const confirm = useConfirmation();
  const history = useHistory();
  const location = useLocation();
  const isPaymentView = location.pathname.endsWith("/payment");
  const goToPayment = () => history.push(`${location.pathname}/payment`);
  const { setDiscountVal, setDiscountFlag } = useDiscount();
  const {
    activeSessionId,
    activeSession,
    openSessions,
    createSession,
    clearActiveSession,
    fetchOpenSessions,
    addItemToSession,
    refreshActiveSession,
    sessionEndedElsewhere,
    acknowledgeSessionEnded,
  } = useOrderSession();
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [openHoldsOpen, setOpenHoldsOpen] = useState(false);
  const [kotDialogOpen, setKotDialogOpen] = useState(false);
  const [kotButtonLoading, setKotButtonLoading] = useState(false);
  const [openSuccessSnackbar] = useSnackbar();
  const [openErrorSnackbar] = useSnackbar();
  const effectiveProfile = useEffectiveProfile();
  const holdFeatureEnabled = !!effectiveProfile?.enableHoldKot;
  

  // Sync cart from session when in hold mode so CartSummary and totals stay correct
  // (also runs when another till changes this held order - live updates
  // reload the session in OrderSessionContext).
  useEffect(() => {
    if (activeSessionId && activeSession?.items) {
      setCartItems(getCartItemsFromSession(activeSession));
    }
  }, [activeSessionId, activeSession]);

  // The held order open here was billed / removed on another till: its items
  // must not stay in this cart (they'd be billed twice).
  useEffect(() => {
    if (!sessionEndedElsewhere) return;
    setCartItems([]);
    setDiscountFlag(false);
    setDiscountVal(0);
    const name = sessionEndedElsewhere.label ? `"${sessionEndedElsewhere.label}" ` : "";
    openErrorSnackbar(
      sessionEndedElsewhere.status === "BILLED"
        ? `Held order ${name}was billed on another till - cart cleared`
        : `Held order ${name}was removed on another till - cart cleared`
    );
    acknowledgeSessionEnded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionEndedElsewhere]);

  const {
    loading: permissionsLoading,
    canUpdatePage,
    canDeletePage,
    canCreatePage,
    isAdmin,
  } = usePermissions();

  // Only hydrate the cart from a loaded invoice while actually editing that
  // invoice. Redux often still holds the last invoice after leaving details
  // or POS payment; copying it here on a new sale is what made old cart
  // lines reappear on /PosMainScreen.
  const isEditInvoice = location.pathname.startsWith("/edit/invoice");
  useEffect(() => {
    if (invoice && !activeSessionId && isEditInvoice) {
      const mappedItems = (invoice.items || []).map(
        ({ item, quantity, rate, originalRate, priceAdjusted, remark }) => {
          const baseRate =
            originalRate ??
            item?.units?.sale?.price ??
            rate ??
            item?.rate ??
            0;
          const effectiveRate = rate ?? baseRate;
          const adjusted =
            priceAdjusted ??
            Math.abs((effectiveRate || 0) - (baseRate || 0)) > 0.0001;
          return {
            ...item,
            quantity,
            originalPrice: baseRate,
            overridePrice: adjusted ? effectiveRate : null,
            priceAdjusted: adjusted,
            remark: remark ?? null,
          };
        }
      );
      hydrateInvoiceCart(mappedItems);
      invoice?.discount ? setDiscountFlag(true) : setDiscountFlag(false);
      setDiscountVal(invoice.discount);
      setVat(invoice.vat);
    } else if (!invoice && !activeSessionId) {
      clearInvoiceStockCredit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, activeSessionId, isEditInvoice]);

  // Fetch open sessions count on mount
  useEffect(() => {
    if (holdFeatureEnabled) {
      fetchOpenSessions();
    }
  }, [fetchOpenSessions, holdFeatureEnabled]);

  useEffect(() => {
    if (!holdFeatureEnabled) {
      setHoldDialogOpen(false);
      setOpenHoldsOpen(false);
    }
  }, [holdFeatureEnabled]);

  const handleHoldBill = async (label) => {
    if (!holdFeatureEnabled) return;
    if (!cartItems.length) return;
    try {
      const session = await createSession(label);
      // Add current cart items to the new session
      for (const item of cartItems) {
        try {
          await addItemToSession(session._id, {
            ...item,
            quantity: item.quantity ?? 1,
          });
        } catch (e) {
          console.error("Failed to add item to session:", e);
        }
      }
      setDiscountFlag(false);
      setDiscountVal(0);
      // Keep session active so KOT button and editing UI show; refresh to get items
      await refreshActiveSession();
      fetchOpenSessions();
      setHoldDialogOpen(false);
      openSuccessSnackbar(`Bill held as "${label}"`);
    } catch (err) {
      openErrorSnackbar(err?.response?.data?.message || "Failed to hold bill");
    }
  };

  const handleNewBill = () => {
    const prevLabel = activeSession?.label;
    clearActiveSession();
    setCartItems([]);
    setDiscountFlag(false);
    setDiscountVal(0);
    if (prevLabel) {
      openSuccessSnackbar(`Saved & exited: ${prevLabel}`);
    }
  };

  // Discards the working cart, not the invoice being edited (if any) - the
  // invoice stays loaded, just emptied out, so someone re-building it from
  // scratch doesn't also lose the "editing INV-XX" context above the list.
  // Session mode has its own equivalent ("New Bill", which also exits the
  // held session) so this stays disabled there rather than offering two
  // overlapping "clear everything" actions with different scopes.
  const handleDiscardCart = async () => {
    if (!cartItems.length || isSessionMode) return;
    const count = cartItems.length;
    const confirmed = await confirm({
      title: "Discard all items?",
      message: `This removes all ${count} item${count === 1 ? "" : "s"} from the cart. This can't be undone.`,
      confirmLabel: "Discard all",
    });
    if (confirmed) {
      setCartItems([]);
      setDiscountFlag(false);
      setDiscountVal(0);
      openSuccessSnackbar("Cart cleared");
    }
  };

  const handleOpenKOT = async () => {
    if (!holdFeatureEnabled) return;
    if (!activeSessionId || !activeSession) return;
    if (!activeSession?.items || activeSession.items.length === 0) return;
    setKotButtonLoading(true);
    try {
      const fresh = await refreshActiveSession();
      setKotDialogOpen(true);
    } finally {
      setTimeout(() => setKotButtonLoading(false), 300);
    }
  };

  const getUnprintedCount = () => {
    if (!activeSession) return 0;
    return (activeSession.items || []).filter((item) => !item.kotPrinted).length;
  };

  const isSessionMode = Boolean(activeSessionId);
  const canProceed = isAdmin() || canCreatePage("Invoices");

  return (
    <Drawer
      className={classes.drawer}
      variant={isCartPersistent ? "persistent" : "temporary"}
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <CartHeader
        classes={classes}
        theme={theme}
        handleDrawerClose={handleDrawerClose}
        invoices={invoices}
      />

      {invoice?._id && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "7px 14px",
            backgroundColor: "#eff6ff",
            borderBottom: "1px solid #bfdbfe",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
            <EditOutlinedIcon style={{ fontSize: 14, color: "#2563eb" }} />
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#2563eb" }}>
              Editing {formatInvoiceNumber(invoice.invoiceNumber)}
            </span>
          </div>
          <span style={{ width: 1, height: 14, backgroundColor: "#bfdbfe", flexShrink: 0 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
            <PersonOutlineOutlinedIcon style={{ fontSize: 15, color: "#64748b", flexShrink: 0 }} />
            <span
              style={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                flexShrink: 0,
              }}
            >
              Customer:
            </span>
            <span
              style={{
                fontSize: "0.75rem",
                color: "#1e293b",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
              title={invoice.client?.name || "N/A"}
            >
              {invoice.client?.name || "N/A"}
            </span>
          </div>
        </div>
      )}

      <Divider />

      <div className={classes.holdActionBar}>
        {holdFeatureEnabled ? (
          <>
            <Button
              size="small"
              variant="outlined"
              className={classes.holdBtn}
              startIcon={<PauseCircleOutlineIcon style={{ fontSize: 16 }} />}
              onClick={() => setHoldDialogOpen(true)}
              disabled={!canProceed || permissionsLoading || cartItems.length === 0 || isSessionMode}
              title={
                isSessionMode
                  ? "Already in a hold - use New Bill first"
                  : cartItems.length === 0
                  ? "Add items to cart first"
                  : "Hold this bill"
              }
            >
              Hold
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={classes.holdBtn}
              startIcon={<FolderOpenOutlinedIcon style={{ fontSize: 16 }} />}
              onClick={() => setOpenHoldsOpen(true)}
              disabled={!canProceed || permissionsLoading}
            >
              Holds ({openSessions?.length || 0})
            </Button>
          </>
        ) : null}
        {isSessionMode && activeSession && (
          <>
            <div className={classes.sessionBadge}>
              <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#ea580c" }}>
                {activeSession.label}
              </span>
              <span style={{ fontSize: "0.6875rem", color: "#64748b", fontWeight: 500 }}>Auto-saved</span>
            </div>
            {holdFeatureEnabled && (
              <Button
                size="small"
                variant="contained"
                className={classes.kotBtn}
                startIcon={kotButtonLoading ? <CircularProgress size={14} style={{ color: "white" }} /> : <RestaurantMenuOutlinedIcon style={{ fontSize: 16 }} />}
                onClick={handleOpenKOT}
                disabled={!activeSessionId || !activeSession || kotButtonLoading}
                style={{
                  backgroundColor: kotButtonLoading ? "#fdba74" : undefined,
                  cursor: activeSessionId && activeSession && !kotButtonLoading ? "pointer" : "not-allowed",
                }}
                title={activeSessionId && activeSession ? "Open KOT dialog" : "No active session"}
              >
                {kotButtonLoading ? "..." : `KOT${getUnprintedCount() > 0 ? ` (${getUnprintedCount()})` : ""}`}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              className={classes.holdBtn}
              startIcon={<AddCircleOutlineIcon style={{ fontSize: 16 }} />}
              onClick={handleNewBill}
            >
              New Bill
            </Button>
          </>
        )}
      </div>

      {holdFeatureEnabled && (
        <>
          <HoldBillDialog
            open={holdDialogOpen}
            onClose={() => setHoldDialogOpen(false)}
            onCreate={handleHoldBill}
          />
          <OpenHoldsPanel
            open={openHoldsOpen}
            onClose={() => setOpenHoldsOpen(false)}
            onLoadSuccess={(label) => openSuccessSnackbar(`Loaded hold: ${label}`)}
            onLoadError={(msg) => openErrorSnackbar(msg || "Failed to load hold")}
          />
          <KOTDialog
            open={kotDialogOpen}
            onClose={() => setKotDialogOpen(false)}
            session={activeSession}
            refreshSession={refreshActiveSession}
            onSuccess={async (msg) => {
              openSuccessSnackbar(msg);
              await refreshActiveSession?.();
              fetchOpenSessions();
            }}
            onError={(msg) => openErrorSnackbar(msg)}
          />
        </>
      )}

      <Divider />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 14px",
          backgroundColor: "#f8fafc",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ShoppingBasketOutlinedIcon style={{ fontSize: 16, color: "#64748b" }} />
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            Cart Items
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tooltip
            title={
              isSessionMode
                ? "Use New Bill to clear a held order"
                : cartItems.length
                ? "Discard all items"
                : "Cart is already empty"
            }
            arrow
          >
            <span>
              <IconButton
                size="small"
                onClick={handleDiscardCart}
                disabled={!cartItems.length || isSessionMode}
                style={{
                  width: 28,
                  height: 28,
                  padding: 5,
                  color: cartItems.length && !isSessionMode ? "#dc2626" : "#cbd5e1",
                }}
              >
                <DeleteSweepOutlinedIcon style={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 22,
              height: 22,
              padding: "0 7px",
              borderRadius: 999,
              backgroundColor: cartItems.length ? "#fff7ed" : "#f1f5f9",
              color: cartItems.length ? "#ea580c" : "#94a3b8",
              border: `1px solid ${cartItems.length ? "#fed7aa" : "#e2e8f0"}`,
              fontSize: "0.75rem",
              fontWeight: 700,
              transition: "all 0.15s ease",
            }}
          >
            {cartItems.length}
          </span>
        </div>
      </div>

      <CartTable currency={currency} />

      <Divider />
      <CartSummary currency={currency} />
      <Divider />

      <Button
        variant="contained"
        color="primary"
        onClick={goToPayment}
        className={classes.payNowButton}
        endIcon={<ArrowForwardIcon style={{ fontSize: 18 }} />}
        disabled={
          cartItems.length === 0 ||
          isPaymentView ||
          permissionsLoading ||
          !canProceed
        }
        style={{
          cursor: permissionsLoading || !canProceed ? "not-allowed" : "pointer",
        }}
      >
        {isSessionMode ? "Final Checkout" : "Save and Continue"}
      </Button>
    </Drawer>
  );
}
export default Cart;
